import { useMemo } from 'react'
import { Icon, Text } from '@/componentsV1/BaseComponents'
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons'
import getNextStepStatusPill, {
  NextStepPillWithClickBehavior,
} from '../NextStepStatusPillRules'
import { useReselector } from '@/utils/sharedHooks'
import { selectMostUrgentModule } from '../yearEndModuleStatus.selectors'
import {
  getSubstepURL,
  useModuleActionButtonBehavior,
} from '../yearEndModuleStatus.helpers'
import { Colors } from '../../../styles/theme'
import { Button } from 'semantic-ui-react'
import { TaxfylePortalError } from '@/features/taxes/annual-taxes/components/TaxfylePortalError'

const OptionalButtonWrapper = ({
  noLink,
  onActionClick,
  children,
  backgroundColor,
  loading,
}: {
  noLink?: boolean
  onActionClick: () => void
  loading?: boolean
  children: React.ReactNode
  backgroundColor: keyof typeof Colors
}) => {
  const style = {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '100px',
    backgroundColor: Colors[backgroundColor],
    padding: '12px 24px',
    gap: 16,
  }

  if (noLink) {
    return <div style={style}>{children}</div>
  }
  return (
    <Button
      style={{ ...style, textAlign: 'start' }}
      onClick={onActionClick}
      loading={loading}
    >
      {children}
      <Icon icon={faArrowRight} />
    </Button>
  )
}

const EOYNextStepPill = ({
  icon,
  title,
  subtext,
  backgroundColor,
  noLink,
  onActionClick,
  modal,
  loading,
  error,
}: NextStepPillWithClickBehavior) => {
  return (
    <>
      <OptionalButtonWrapper
        noLink={noLink}
        onActionClick={onActionClick}
        backgroundColor={backgroundColor}
        loading={loading}
      >
        <div>{icon}</div>
        <div>
          {title && (
            <Text as="bodySm" style={{ fontWeight: 500 }}>
              {title}
            </Text>
          )}
          <Text as="bodyXs">{subtext}</Text>
        </div>
      </OptionalButtonWrapper>
      <TaxfylePortalError error={error ?? null} />
      {modal}
    </>
  )
}

export default EOYNextStepPill

export const useYearEndNextStepPill = () => {
  const mostUrgentEndModuleStatus = useReselector(selectMostUrgentModule)
  const moduleType = mostUrgentEndModuleStatus?.moduleType
  const substepURL = useMemo(() => {
    return getSubstepURL(
      mostUrgentEndModuleStatus?.year,
      mostUrgentEndModuleStatus?.substepIdentifier
    )
  }, [
    mostUrgentEndModuleStatus?.year,
    mostUrgentEndModuleStatus?.substepIdentifier,
  ])
  const buttonBehavior = useModuleActionButtonBehavior(
    moduleType ?? null,
    mostUrgentEndModuleStatus?.status ?? null,
    substepURL,
    mostUrgentEndModuleStatus?.substepIdentifier
  )
  const props = getNextStepStatusPill({
    moduleType,
    status: mostUrgentEndModuleStatus?.status,
    substepIdentifier: mostUrgentEndModuleStatus?.substepIdentifier,
    dueDate: mostUrgentEndModuleStatus?.dueDate,
  })
  return <EOYNextStepPill {...props} {...buttonBehavior} />
}
